/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
// src/components/ResourceManager/CloudConnectivity/CloudConnectivityInventoryPage.tsx

import React, { useState, useEffect, useMemo } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { columns as baseColumns, CloudConnectivity, shapeCloudConnectivityData } from "./CloudConnectivityData";
import {
  getCloudConnectivityServices,
  getCloudConnectivityServiceById,
  addCloudConnectivityService,
  updateCloudConnectivityService,
  deleteCloudConnectivityService,
  AddCloudConnectivityPayload,
  UpdateCloudConnectivityPayload,
} from "./request/request";
import { getKEPorts } from "../../Port/request/request";

import { CloudConnectivityViewModal, CloudConnectivityEditModal, EditForm } from "./CloudModals";

// UI components
import { GenericTable } from "@ui-components/Table/GenericTable";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import GenericCard from "@ui-components/Card/GenericCard";
import GenericSearchbar, { ParsedFilter } from "@ui-components/Searchbar/GenericSearchbar";

// Icons
import { ReactComponent as IconEdit } from "../../../../../shared/assets/Icons/Icon_Edit.svg";
import { ReactComponent as IconDelete } from "../../../../../shared/assets/Icons/Icon_Delete.svg";
import { ReactComponent as IconEye } from "../../../../../shared/assets/Icons/Icon_Eye.svg";
import { ReactComponent as IconPlus } from "../../../../../shared/assets/Icons/IconPlusNV.svg";

import { useAppSelector } from "@redux/hooks";
import { buildPredicate, FilterField, labelId, ParsedFilter as OldParsed, parseToken } from "../../../../helpers/advancedFilters";

export default function CloudConnectivityPage() {
  // ========= 1) Table data =========
  const [cloudConnectivityServices, setCloudConnectivityServices] = useState<CloudConnectivity[]>([]);
  const [isFetching, setIsFetching] = useState(false);

  // ========= 2) Add/Edit modal state & fields =========
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState<EditForm>({
    name: "",
    mbps: null,
    tag: [],
  });
  const [originalEditValues, setOriginalEditValues] = useState<EditForm>({
    name: "",
    mbps: null,
    tag: [],
  });
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedCloudConnectivityId, setSelectedCloudConnectivityId] = useState<number | string | null>(null);

  // Create form state
  interface CreateForm {
    kontrolEdgePortId: number | null;
    mbps: number | null;
    vlan: number | null;
    automaticVlan: boolean | null;
    subscriptionId: string;
    terms: number | null;
    name: string;
    tag: string[];
    provider: string;
    providerId: string;
    providerAccount: number | null;
    cloud: string;
    account: string;
    monitoring: {
      enable: boolean | null;
      maintenance_mode: boolean | null;
    };
  }

  const [createForm, setCreateForm] = useState<CreateForm>({
    kontrolEdgePortId: null,
    mbps: null,
    vlan: null,
    automaticVlan: null,
    subscriptionId: "",
    terms: null,
    name: "",
    tag: [],
    provider: "",
    providerId: "",
    providerAccount: null,
    cloud: "",
    account: "",
    monitoring: {
      enable: null,
      maintenance_mode: null,
    },
  });

  // Port options for dropdown
  const [portOptions, setPortOptions] = useState<Array<{ id: number; label: string }>>([]);
  const [isCreating, setIsCreating] = useState(false);

  // ========= 3) View modal state =========
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedCloudConnectivity, setSelectedCloudConnectivity] = useState<CloudConnectivity | null>(null);
  const [isViewLoading, setIsViewLoading] = useState(false);

  // ========= 4) Delete confirmation state =========
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [cloudConnectivityToDelete, setCloudConnectivityToDelete] = useState<CloudConnectivity | null>(null);

  // ========== Auth0 ==========
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const { tenantId, partnerId } = useAppSelector((state) => state.selection);
  const isViewingAllCustomers = tenantId === partnerId;

  // ========== Filters ==========
  const [activeFilters, setActiveFilters] = useState<OldParsed[]>([]);

  // ------------------------------------------------------------------------------------
  // useEffect: Fetch Cloud Connectivity services on mount
  // ------------------------------------------------------------------------------------
  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) return;

    const fetchCloudConnectivityServices = async () => {
      try {
        setIsFetching(true);
        const token = await getAccessTokenSilently();
        const response = await getCloudConnectivityServices(token);
        const raw = (response as any)?.data?.data ?? (response as any)?.data ?? [];
        const shapedData = shapeCloudConnectivityData(raw);
        setCloudConnectivityServices(shapedData);
      } catch (err) {
        console.error("Error fetching Cloud Connectivity services:", err);
      } finally {
        setIsFetching(false);
      }
    };

    fetchCloudConnectivityServices();
  }, [isLoading, isAuthenticated, getAccessTokenSilently]);

  // ------------------------------------------------------------------------------------
  // Fetch ports for dropdown
  // ------------------------------------------------------------------------------------
  useEffect(() => {
    if (isLoading || !isAuthenticated) return;

    const fetchPorts = async () => {
      try {
        const token = await getAccessTokenSilently();
        const response = await getKEPorts(token);
        // getKEPorts returns { success: true, message: "...", data: [...] }
        const raw = (response as any)?.data?.data ?? [];
        const ports = raw.map((port: any) => ({
          id: typeof port.id === 'number' ? port.id : Number(port.id),
          label: port.name || port.portName || String(port.id),
        }));
        setPortOptions(ports);
      } catch (err) {
        console.error("Error fetching ports:", err);
      }
    };

    fetchPorts();
  }, [isLoading, isAuthenticated, getAccessTokenSilently]);

  // ------------------------------------------------------------------------------------
  // Filter logic
  // ------------------------------------------------------------------------------------
  function makeCloudConnectivityFields(): FilterField<CloudConnectivity>[] {
    return [
      { label: "Name", key: "name", type: "string", accessor: (r) => r.name, inFreeText: true },
      { label: "Subscription ID", key: "subscriptionid", type: "string", accessor: (r) => r.subscriptionId, inFreeText: true },
      { label: "Status", key: "status", type: "string", accessor: (r) => (r.isDraft ? "Draft" : "Active"), inFreeText: true },
    ];
  }

  const cloudConnectivityFields = useMemo(() => makeCloudConnectivityFields(), []);
  const fieldById = useMemo(() => {
    const m = new Map<string, FilterField<CloudConnectivity>>();
    for (const f of cloudConnectivityFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      m.set(id, f);
    }
    return m;
  }, [cloudConnectivityFields]);

  const keyMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of cloudConnectivityFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      const label = f.label.toLowerCase();
      m.set(id, id);
      m.set(label, id);
    }
    return m;
  }, [cloudConnectivityFields]);

  const normalizedFilters = useMemo<ParsedFilter[]>(() => {
    return activeFilters.map((f) => {
      const token = f.op ? { ...f } : parseToken(f.key ? `${f.key}=${f.value}` : f.value);

      if (token.key) {
        const k = token.key.trim().toLowerCase();
        token.key = keyMap.get(k) ?? labelId(token.key);
      }

      const field = token.key ? fieldById.get(token.key) : undefined;
      if (field?.type === "string" && token.value) {
        const quoted = token.value.match(/^"(.*)"$/);
        if (quoted) {
          token.value = quoted[1];
          token.op = "=";
        } else if (!token.op) {
          token.op = ":";
        }
      }

      return token;
    });
  }, [activeFilters, keyMap, fieldById]);

  const predicate = useMemo(
    () => buildPredicate<CloudConnectivity>(normalizedFilters, cloudConnectivityFields),
    [normalizedFilters, cloudConnectivityFields]
  );

  const filteredData = useMemo(
    () => cloudConnectivityServices.filter(predicate),
    [cloudConnectivityServices, predicate]
  );

  const suggestedKeys = useMemo(() => {
    const headers = baseColumns
      .map((c) => c.header)
      .filter(Boolean) as string[];

    if (isViewingAllCustomers && !headers.includes("Customer")) {
      headers.splice(1, 0, "Customer");
    }
    return headers;
  }, [baseColumns, isViewingAllCustomers]);

  // ------------------------------------------------------------------------------------
  // Handle ADD
  // ------------------------------------------------------------------------------------
  const handleAddCloudConnectivity = () => {
    setEditMode(false);
    setIsModalOpen(true);
    // Reset create form
    setCreateForm({
      kontrolEdgePortId: null,
      mbps: null,
      vlan: null,
      automaticVlan: null,
      subscriptionId: "",
      terms: null,
      name: "",
      tag: [],
      provider: "",
      providerId: "",
      providerAccount: null,
      cloud: "",
      account: "",
      monitoring: {
        enable: null,
        maintenance_mode: null,
      },
    });
  };

  // ------------------------------------------------------------------------------------
  // Handle EDIT
  // ------------------------------------------------------------------------------------
  const handleEditCloudConnectivity = async (cloudConnectivity: CloudConnectivity) => {
    setEditMode(true);
    setIsModalOpen(true);
    setIsEditLoading(true);
    setSelectedCloudConnectivityId(cloudConnectivity.id);
    setSelectedCloudConnectivity(null);
    setEditForm({ name: "", mbps: null, tag: [] });
    setOriginalEditValues({ name: "", mbps: null, tag: [] });

    try {
      const token = await getAccessTokenSilently();
      const response = await getCloudConnectivityServiceById(token, cloudConnectivity.id);
      const raw = (response as any)?.data?.data ?? (response as any)?.data;

      if (raw) {
        // Shape the detail response with all fields
        const shapedData: CloudConnectivity = {
          id: raw.id ?? cloudConnectivity.id,
          name: raw.name ?? cloudConnectivity.name,
          tag: Array.isArray(raw.tag) ? raw.tag.filter(Boolean) : (cloudConnectivity.tag ?? []),
          isDraft: Boolean(raw.isDraft ?? cloudConnectivity.isDraft),
          subscriptionId: raw.subscriptionId ?? cloudConnectivity.subscriptionId,
          tenantNameDisplay: raw.kontrolEdgePort?.tenant?.nameDisplay ?? cloudConnectivity.tenantNameDisplay,
          // Detail response fields
          kontrolEdgePortId: raw.kontrolEdgePortId,
          mbps: raw.mbps,
          vlan: raw.vlan,
          automaticVlan: Boolean(raw.automaticVlan),
          terms: raw.terms,
          autoRenew: raw.autoRenew,
          kontrolEdgeLagId: raw.kontrolEdgeLagId,
          provider: raw.provider,
          providerId: raw.providerId,
          providerAccount: raw.providerAccount,
          cloud: raw.cloud,
          account: raw.account,
          monitoring: raw.monitoring,
          createdAt: raw.createdAt,
          updatedAt: raw.updatedAt,
          odooMetadata: raw.odooMetadata,
          kontrolEdgePort: raw.kontrolEdgePort,
        };
        setSelectedCloudConnectivity(shapedData);
        // Set editable fields
        const name = shapedData.name ?? "";
        const mbps = typeof shapedData.mbps === 'number' ? shapedData.mbps : null;
        const tag = Array.isArray(shapedData.tag) ? shapedData.tag.filter(Boolean) : [];
        setEditForm({ name, mbps, tag });
        setOriginalEditValues({ name, mbps, tag });
      }
    } catch (error) {
      console.error("Error fetching Cloud Connectivity service details for edit:", error);
      // Fallback to table data
      setSelectedCloudConnectivity(cloudConnectivity);
      const name = cloudConnectivity.name ?? "";
      const mbps = typeof cloudConnectivity.mbps === 'number' ? cloudConnectivity.mbps : null;
      const tag = Array.isArray(cloudConnectivity.tag) ? cloudConnectivity.tag.filter(Boolean) : [];
      setEditForm({ name, mbps, tag });
      setOriginalEditValues({ name, mbps, tag });
    } finally {
      setIsEditLoading(false);
    }
  };

  // ------------------------------------------------------------------------------------
  // Handle VIEW
  // ------------------------------------------------------------------------------------
  const handleViewCloudConnectivity = async (cloudConnectivity: CloudConnectivity) => {
    setIsViewModalOpen(true);
    setIsViewLoading(true);
    setSelectedCloudConnectivity(null);

    try {
      const token = await getAccessTokenSilently();
      const response = await getCloudConnectivityServiceById(token, cloudConnectivity.id);
      const raw = (response as any)?.data?.data ?? (response as any)?.data;

      if (raw) {
        // Shape the detail response with all fields
        const shapedData: CloudConnectivity = {
          id: raw.id ?? cloudConnectivity.id,
          name: raw.name ?? cloudConnectivity.name,
          tag: Array.isArray(raw.tag) ? raw.tag.filter(Boolean) : (cloudConnectivity.tag ?? []),
          isDraft: Boolean(raw.isDraft ?? cloudConnectivity.isDraft),
          subscriptionId: raw.subscriptionId ?? cloudConnectivity.subscriptionId,
          tenantNameDisplay: raw.kontrolEdgePort?.tenant?.nameDisplay ?? cloudConnectivity.tenantNameDisplay,
          // Detail response fields
          kontrolEdgePortId: raw.kontrolEdgePortId,
          mbps: raw.mbps,
          vlan: raw.vlan,
          automaticVlan: Boolean(raw.automaticVlan),
          terms: raw.terms,
          autoRenew: raw.autoRenew,
          kontrolEdgeLagId: raw.kontrolEdgeLagId,
          provider: raw.provider,
          providerId: raw.providerId,
          providerAccount: raw.providerAccount,
          cloud: raw.cloud,
          account: raw.account,
          monitoring: raw.monitoring,
          createdAt: raw.createdAt,
          updatedAt: raw.updatedAt,
          odooMetadata: raw.odooMetadata,
          kontrolEdgePort: raw.kontrolEdgePort,
        };
        setSelectedCloudConnectivity(shapedData);
      }
    } catch (error) {
      console.error("Error fetching Cloud Connectivity service details:", error);
      setSelectedCloudConnectivity(cloudConnectivity);
    } finally {
      setIsViewLoading(false);
    }
  };

  // ------------------------------------------------------------------------------------
  // Handle DELETE
  // ------------------------------------------------------------------------------------
  const handleDeleteClick = (cloudConnectivity: CloudConnectivity) => {
    setCloudConnectivityToDelete(cloudConnectivity);
    setIsAlertOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!cloudConnectivityToDelete) return;
    try {
      setIsFetching(true);
      const token = await getAccessTokenSilently();
      await deleteCloudConnectivityService(token, cloudConnectivityToDelete.id);

      setCloudConnectivityServices((prev) =>
        prev.filter((item) => item.id !== cloudConnectivityToDelete.id)
      );
      setCloudConnectivityToDelete(null);
      setIsAlertOpen(false);
    } catch (error) {
      console.error("Error deleting Cloud Connectivity service:", error);
    } finally {
      setIsFetching(false);
    }
  };

  const handleCancelDelete = () => {
    setCloudConnectivityToDelete(null);
    setIsAlertOpen(false);
  };

  // ------------------------------------------------------------------------------------
  // Handle SAVE (Update)
  // ------------------------------------------------------------------------------------
  const handleSaveCloudConnectivity = async () => {
    if (!selectedCloudConnectivityId || !selectedCloudConnectivity) return;

    try {
      setIsSaving(true);
      const token = await getAccessTokenSilently();
      
      const payload: UpdateCloudConnectivityPayload = {
        name: editForm.name,
        mbps: editForm.mbps ?? 0,
        tag: editForm.tag,
      };

      await updateCloudConnectivityService(token, selectedCloudConnectivityId, payload);
      
      // Refresh the list
      const response = await getCloudConnectivityServices(token);
      const raw = (response as any)?.data?.data ?? (response as any)?.data ?? [];
      const shapedData = shapeCloudConnectivityData(raw);
      setCloudConnectivityServices(shapedData);
      
      setIsModalOpen(false);
      setEditMode(false);
      setSelectedCloudConnectivityId(null);
      setSelectedCloudConnectivity(null);
      setEditForm({ name: "", mbps: null, tag: [] });
      setOriginalEditValues({ name: "", mbps: null, tag: [] });
    } catch (error) {
      console.error("Error updating Cloud Connectivity service:", error);
    } finally {
      setIsSaving(false);
    }
  };

  // Check if there are changes in editable fields
  const hasChanges = useMemo(() => {
    if (!editMode) return false;
    return (
      editForm.name !== originalEditValues.name ||
      editForm.mbps !== originalEditValues.mbps ||
      JSON.stringify(editForm.tag.sort()) !== JSON.stringify(originalEditValues.tag.sort())
    );
  }, [editForm, originalEditValues, editMode]);

  const handleEditFieldChange = (field: "name" | "mbps" | "tag", value: string | number | string[]) => {
    setEditForm((prev) => ({
      ...prev,
      [field]: field === "mbps" ? (value ? Number(value) : null) : field === "tag" ? (value as string[]) : String(value),
    }));
  };

  // ------------------------------------------------------------------------------------
  // Handle CREATE
  // ------------------------------------------------------------------------------------
  const handleCreateFieldChange = (
    field: string,
    value: string | number | boolean | null | string[] | { enable: boolean | null; maintenance_mode: boolean | null }
  ) => {
    if (field === "monitoring") {
      setCreateForm((prev) => ({
        ...prev,
        monitoring: value as { enable: boolean | null; maintenance_mode: boolean | null },
      }));
    } else {
      setCreateForm((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handlePortSelect = (portLabel: string) => {
    const port = portOptions.find((p) => p.label === portLabel);
    if (port) {
      handleCreateFieldChange("kontrolEdgePortId", port.id);
    }
  };

  const getPortLabelById = (id: number | null): string => {
    if (id === null) return "";
    const port = portOptions.find((p) => p.id === id);
    return port ? port.label : "";
  };

  // Validation for create form
  const isCreateFormValid = useMemo(() => {
    return (
      createForm.kontrolEdgePortId !== null &&
      createForm.mbps !== null &&
      createForm.vlan !== null &&
      createForm.automaticVlan !== null &&
      createForm.terms !== null &&
      createForm.subscriptionId.trim() !== "" &&
      createForm.name.trim() !== "" &&
      createForm.provider.trim() !== "" &&
      createForm.providerId.trim() !== "" &&
      createForm.providerAccount !== null &&
      createForm.cloud.trim() !== "" &&
      createForm.account.trim() !== "" &&
      createForm.monitoring.enable !== null &&
      createForm.monitoring.maintenance_mode !== null
    );
  }, [createForm]);

  // Handle create Cloud Connectivity service
  const handleCreateCloudConnectivity = async () => {
    if (!isCreateFormValid) return;

    // Ensure all required fields are non-null before proceeding
    const {
      kontrolEdgePortId, mbps, vlan, automaticVlan, subscriptionId,
      terms, name, tag, provider, providerId, providerAccount,
      cloud, account, monitoring
    } = createForm;

    if (
      kontrolEdgePortId === null || mbps === null || vlan === null ||
      automaticVlan === null || terms === null || providerAccount === null ||
      monitoring.enable === null || monitoring.maintenance_mode === null
    ) {
      console.error("Attempted to create Cloud Connectivity service with incomplete data.");
      return;
    }

    try {
      setIsCreating(true);
      const token = await getAccessTokenSilently();

      const payload: AddCloudConnectivityPayload = {
        kontrolEdgePortId: kontrolEdgePortId,
        mbps: mbps,
        vlan: vlan,
        automaticVlan: automaticVlan,
        subscriptionId: subscriptionId,
        terms: terms,
        name: name,
        tag: tag,
        provider: provider,
        providerId: providerId,
        providerAccount: providerAccount,
        cloud: cloud,
        account: account,
        monitoring: {
          enable: monitoring.enable,
          maintenance_mode: monitoring.maintenance_mode,
        },
      };

      await addCloudConnectivityService(token, payload);

      // Refresh the list
      const response = await getCloudConnectivityServices(token);
      const raw = (response as any)?.data?.data ?? (response as any)?.data ?? [];
      const shapedData = shapeCloudConnectivityData(raw);
      setCloudConnectivityServices(shapedData);

      // Close modal and reset form
      setIsModalOpen(false);
      setCreateForm({
        kontrolEdgePortId: null,
        mbps: null,
        vlan: null,
        automaticVlan: null,
        subscriptionId: "",
        terms: null,
        name: "",
        tag: [],
        provider: "",
        providerId: "",
        providerAccount: null,
        cloud: "",
        account: "",
        monitoring: {
          enable: null,
          maintenance_mode: null,
        },
      });
    } catch (error) {
      console.error("Error creating Cloud Connectivity service:", error);
    } finally {
      setIsCreating(false);
    }
  };

  // ------------------------------------------------------------------------------------
  // Override "Actions" column
  // ------------------------------------------------------------------------------------
  const columns = [
    ...baseColumns,
    {
      header: "Actions",
      accessor: "name" as keyof CloudConnectivity,
      renderCell: (row: CloudConnectivity) => (
        <div className="flex items-center gap-2">
          {/* View Button */}
          <button
            className="
              flex items-center justify-center
              border border-semantic-border-secondary
              rounded
              text-semantic-text-secondary
              hover:bg-semantic-state-active
              w-10 h-10
            "
            onClick={(e) => {
              e.stopPropagation();
              handleViewCloudConnectivity(row);
            }}
            title="View details"
          >
            <IconEye className="w-5 h-5" />
          </button>

          {/* Edit Button */}
          <button
            className="
              flex items-center justify-center
              border border-semantic-border-secondary
              rounded
              text-semantic-text-secondary
              hover:bg-semantic-state-active
              w-10 h-10
            "
            onClick={(e) => {
              e.stopPropagation();
              handleEditCloudConnectivity(row);
            }}
            title="Edit"
          >
            <IconEdit className="w-9 h-9" />
          </button>

          {/* Delete Button */}
          <button
            className="
              flex items-center justify-center
              p-2
              border border-semantic-border-secondary
              rounded
              bg-red-50
              text-red-600
              hover:bg-red-100
              w-10 h-10
            "
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteClick(row);
            }}
            title="Delete"
          >
            <IconDelete className="w-5 h-5" />
          </button>
        </div>
      ),
    },
  ];

  // ------------------------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------------------------
  return (
    <GenericCard isCollapsible={false}>
      {/* Header */}
      <div className="mb-4 p-5 bg-[var(--semantic-bg-primary)]">
        <div className="flex items-end justify-between">
          <div className="w-[520px] my-5">
            <GenericSearchbar
              placeholder="Find by attribute or value"
              suggestedKeys={suggestedKeys}
              onFiltersChange={setActiveFilters}
            />
          </div>
          <div className="mb-10">
            <GenericButton
              label={
                <div className="flex items-center gap-2">
                  <IconPlus className="w-5 h-5" />
                  <span>Add new Cloud Connectivity service</span>
                </div>
              }
              variant="brand"
              size="md"
              onClick={handleAddCloudConnectivity}
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="px-5 mb-5">
        <GenericTable
          columns={columns}
          data={filteredData}
          initialRowsPerPage={5}
          rowsPerPageOptions={[5, 10]}
          tableClassName="bg-[var(--semantic-bg-primary)]"
          width="100%"
        />
      </div>

      {/* ====================== Modal Add / Edit ====================== */}
      <CloudConnectivityEditModal
        isOpen={isModalOpen}
        editMode={editMode}
        isLoading={isEditLoading}
        cloudConnectivity={selectedCloudConnectivity}
        editForm={editForm}
        createForm={createForm}
        portOptions={portOptions}
        hasChanges={hasChanges}
        isFormValid={isCreateFormValid}
        isSaving={isSaving}
        isCreating={isCreating}
        onClose={() => {
          setIsModalOpen(false);
          setEditMode(false);
          setSelectedCloudConnectivityId(null);
          setSelectedCloudConnectivity(null);
          setEditForm({ name: "", mbps: null, tag: [] });
          setOriginalEditValues({ name: "", mbps: null, tag: [] });
          setCreateForm({
            kontrolEdgePortId: null,
            mbps: null,
            vlan: null,
            automaticVlan: null,
            subscriptionId: "",
            terms: null,
            name: "",
            tag: [],
            provider: "",
            providerId: "",
            providerAccount: null,
            cloud: "",
            account: "",
            monitoring: {
              enable: null,
              maintenance_mode: null,
            },
          });
        }}
        onSave={handleSaveCloudConnectivity}
        onCreate={handleCreateCloudConnectivity}
        onEditFieldChange={handleEditFieldChange}
        onCreateFieldChange={handleCreateFieldChange}
        onPortSelect={handlePortSelect}
        getPortLabelById={getPortLabelById}
      />

      {/* ====================== Modal View (read-only) ====================== */}
      <CloudConnectivityViewModal
        isOpen={isViewModalOpen}
        isLoading={isViewLoading}
        cloudConnectivity={selectedCloudConnectivity}
        onClose={() => setIsViewModalOpen(false)}
      />

      {/* ====================== ALERT: confirm Delete ====================== */}
      <GenericAlert
        isOpen={isAlertOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Are you sure?"
        description="This action cannot be undone. This will permanently delete the Cloud Connectivity service from the inventory."
        cancelLabel="Cancel"
        confirmLabel="Delete"
        iconWidthClass="w-20 h-12"
      />

      {/* Overlay spinner */}
      {isFetching && (
        <div
          className="
            fixed
            inset-0
            flex flex-col items-center justify-center
            bg-black/50
            z-[9999]
          "
        >
          <h2 className="text-xl font-semibold mb-4 text-white">
            Processing request...
          </h2>
          <div
            className="
              animate-spin
              rounded-full
              h-12 w-12
              border-4
              border-gray-300
              border-t-blue-500
            "
          />
        </div>
      )}
    </GenericCard>
  );
}

